import React,{ useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  Button,
  Conditional,
  Embed,
  Image,
  Modal,
  ModalContent,
  Space,
  Text,
} from "@cashfree-intl/coherent";

import { EMBED_LINKS } from "containers/ProductTour/constants";
import { HybridOptionButton, SubStepDescription } from "containers/CheckoutJourney/screens/styled";
import {
  makeSelectPlatform,
  makeSelectPlatformOption,
} from "containers/CheckoutJourney/selector";
import {
  INTEGRATION_STATUS,
  apiDocsReference,
  hybridOptions,
} from "containers/CheckoutJourney/util/constant";
import websdkVideo from "images/CheckoutJourney/websdkVideo.svg";
import { sendAnalyticsData } from "containers/ProductTour/helper";
import { StyledModal } from "containers/CheckoutJourney/styled";
import { SubstepComponentProps } from 'containers/CheckoutJourney/types';


const SandboxIntegration = ({ substep, updateStatus }: SubstepComponentProps) => {
  const platform = useSelector(makeSelectPlatform());
  const platformOption = useSelector(makeSelectPlatformOption());

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedHybrid, setSelectedHybrid] = useState<string>("flutter");
  const [selectedHybridData, setSelectedHybridData] = useState<{
    video: string;
    apiReference: string;
  }>({
    video: EMBED_LINKS.API_INTEGRATION_MOBILE_HYBRID_FLUTTER,
    apiReference: apiDocsReference.MOBILE_HYBRID_FLUTTER,
  });

  const handleModal = (state: boolean) => setModalOpen(state);
  const markAsDone = () => {
    updateStatus(substep.id, INTEGRATION_STATUS.COMPLETED);
    sendAnalyticsData("integrateInSandboxButtonClicked");
  };

  const onClickApiReference = () => {
    const mobileMapping: Record<string, string> = {
      hybrid: selectedHybridData.apiReference,
      iOS: apiDocsReference.MOBILE_IOS,
      android: apiDocsReference.MOBILE_ANDROID,
    };
    const url =
      platform === "web"
        ? apiDocsReference.WEB
        : mobileMapping[platformOption] || apiDocsReference.WEB;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getEmbedUrl = () => {
    if (platform === "web") return EMBED_LINKS.API_INTEGRATION_WEB;
    const mobileEmbedLinks: Record<string, string> = {
      android: EMBED_LINKS.API_INTEGRATION_MOBILE_ANDROID,
      iOS: EMBED_LINKS.API_INTEGRATION_MOBILE_IOS,
      hybrid: selectedHybridData.video,
    };
    return mobileEmbedLinks[platformOption] || EMBED_LINKS.API_INTEGRATION_WEB;
  };

  const handleHybridSelection = (option: string) => {
    setSelectedHybrid(option);
    setSelectedHybridData({
      video: hybridOptions[option]?.video || selectedHybridData.video,
      apiReference: hybridOptions[option]?.apiReference || selectedHybridData.apiReference,
    });
  };

  return (
    <Space gap={2.5} direction="column" className="mt-2">
      <Space direction="row" justifyContent="space-between" alignItems="flex-start" fullWidth>
        <Space direction="column" style={{ flex: 3 }}>
          <SubStepDescription color="bodyLight">
            Follow this video guide or{" "}
            <Text as="span" color="primary" style={{ cursor: "pointer" }} onClick={onClickApiReference}>
              API reference
            </Text>{" "}
            to integrate our checkout in your test environment.
          </SubStepDescription>
          <SubStepDescription color="bodyLight">
            You can refresh status to check if the test transaction is registered at our end.
          </SubStepDescription>
        </Space>

        <Space direction="column" gap={1} alignItems="center">
          <Conditional if={platformOption === "hybrid"}>
            <Space gap={1}>
              {["flutter", "reactNative", "cordova"].map((option) => (
                <HybridOptionButton
                  secondary
                  key={option}
                  isSelected={selectedHybrid === option}
                  onClick={() => handleHybridSelection(option)}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </HybridOptionButton>
              ))}
            </Space>
          </Conditional>
          <Image
            src={websdkVideo}
            alt="websdk-video-thumbnail"
            width="140px"
            height="105px"
            onClick={() => handleModal(true)}
            className="pointer"
          />
        </Space>
      </Space>

      <Space>
        <Button primary onClick={markAsDone}>Start Integration</Button>
      </Space>

      <StyledModal open={modalOpen} onClose={() => handleModal(false)}>
        <ModalContent className="p-0">
          <Embed
            active
            placeholder="Activate on Shopify"
            url={getEmbedUrl()}
            autoplay
            iframe={{ allowFullScreen: true }}
          />
        </ModalContent>
      </StyledModal>
    </Space>
  );
};

export { SandboxIntegration };
